import ResponsiveRow from '../shared/ResponsiveRow';
import '../financials/financialsResponsive.css';
import { useState, useEffect, useMemo, useCallback } from 'react';

function ProfitLossView({ transactions }) {
    const [dateRange, setDateRange] = useState('currentMonth');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    
    // Calculate date range
    const getDateRange = () => {
        const now = new Date();
        let start, end = now;
        
        switch(dateRange) {
            case 'currentMonth':
                start = new Date(now.getFullYear(), now.getMonth(), 1);
                break;
            case 'quarter':
                const quarter = Math.floor(now.getMonth() / 3);
                start = new Date(now.getFullYear(), quarter * 3, 1);
                break;
            case 'currentYear':
                start = new Date(now.getFullYear(), 0, 1);
                break;
            case 'custom':
                start = customStartDate ? new Date(customStartDate) : new Date(now.getFullYear(), 0, 1);
                end = customEndDate ? new Date(customEndDate) : now;
                break;
            default:
                start = new Date(now.getFullYear(), now.getMonth(), 1);
        }
        return { start, end };
    };
    
    const { start: startDate, end: endDate } = getDateRange();
    
    // Filter transactions by date range
    const filteredTransactions = transactions.filter(t => {
        const transDate = new Date(t.date);
        return transDate >= startDate && transDate <= endDate;
    });
    
    const income = filteredTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + (t.amount || 0), 0);
    const expenses = filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
    const netIncome = income - expenses;
    const profitMargin = income > 0 ? ((netIncome / income) * 100).toFixed(1) : 0;
     return (
        <div className="ia-financials">
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Profit & Loss Statement</h2>
                <p className="text-sm text-gray-600 mt-1">Income statement for the selected period</p>
            </div>
             {/* Date Range Selector */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-6">
                <ResponsiveRow className="ia-financial-filters flex flex-wrap items-center gap-4">
                    <label className="font-medium text-gray-700">Period:</label>
                    <select 
                        value={dateRange} 
                        onChange={(e) => setDateRange(e.target.value)}
                        className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)] font-medium"
                    >
                        <option value="currentMonth">Current Month</option>
                        <option value="quarter">Quarter</option>
                        <option value="currentYear">Current Year</option>
                        <option value="custom">Custom Range</option>
                    </select>
                    
                    {dateRange === 'custom' && (
                        <div className="ia-financial-date-range flex items-center gap-2">
                            <input 
                                type="date" 
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                                className="px-3 py-2 border-2 border-gray-300 rounded-lg"
                            />
                            <span className="text-gray-600">to</span>
                            <input 
                                type="date" 
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                                className="px-3 py-2 border-2 border-gray-300 rounded-lg"
                            />
                        </div>
                    )}
                </ResponsiveRow>
            </div>
             {/* P&L Statement */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-6">
                    {/* Revenue Section */}
                    <div className="mb-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                            📈 Revenue
                        </h3>
                        <div className="flex justify-between items-center py-3 border-b">
                            <span className="text-gray-700">Total Income</span>
                            <span className="text-lg font-bold text-green-600">
                                ${income.toLocaleString('en-US', {minimumFractionDigits: 2})}
                            </span>
                        </div>
                    </div>
                     {/* Expenses Section */}
                    <div className="mb-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                            📉 Expenses
                        </h3>
                        <div className="flex justify-between items-center py-3 border-b">
                            <span className="text-gray-700">Total Expenses</span>
                            <span className="text-lg font-bold text-red-600">
                                ${expenses.toLocaleString('en-US', {minimumFractionDigits: 2})}
                            </span>
                        </div>
                    </div>
                     {/* Net Income Section */}
                    <div className="pt-4 border-t-2 border-gray-300">
                        <div className="flex justify-between items-center py-3 bg-gray-50 px-4 rounded-lg mb-2">
                            <span className="text-xl font-bold text-gray-800">Net Income</span>
                            <span className={`text-2xl font-bold ${netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                ${netIncome.toLocaleString('en-US', {minimumFractionDigits: 2})}
                            </span>
                        </div>
                        <div className="flex justify-between items-center py-2 px-4">
                            <span className="text-sm text-gray-600">Profit Margin</span>
                            <span className={`text-lg font-semibold ${profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {profitMargin}%
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}


export default ProfitLossView;
